<template>
  <div ref="routeNavigatorRef" class="route-navigator" :class="{
    'collapsed': isCollapsed,
    'expanded': !isCollapsed,
  }">
    <!-- Toast notification -->
    <transition name="toast">
      <div v-if="showToast" class="toast-notification" :class="toastType">
        <span class="toast-icon">{{ toastIcon }}</span>
        <span class="toast-message">{{ toastMessage }}</span>
      </div>
    </transition>

    <!-- Header del navegador -->
    <div class="navigator-header">
      <div class="header-title">
        <span class="material-icons">route</span>
        <h3>Explorar Rutas</h3>
      </div>
      <div class="header-actions">
        <button @click="toggleSearch" class="action-btn" :class="{ 'active': showSearch }" aria-label="Buscar rutas">
          <span class="material-icons">search</span>
        </button>

        <!-- Menú desplegable de selección múltiple -->
        <div class="select-dropdown">
          <button @click="toggleSelectMenu" class="action-btn"
            :class="{ 'active': selectMenuOpen, 'painting': isPainting }"
            :aria-label="allRoutesSelected ? 'Deseleccionar todas las rutas' : 'Opciones de selección'"
            :title="allRoutesSelected ? 'Deseleccionar todas' : 'Opciones de selección'">
            <span class="material-icons">{{ isPainting ? 'pause' : (allRoutesSelected ? 'deselect' : 'select_all')
              }}</span>
            <span v-if="isPainting" class="painting-indicator"></span>
          </button>

          <!-- Menú de opciones con botón de cierre -->
          <transition name="slide-down">
            <div v-if="selectMenuOpen" class="select-dropdown-menu">
              <div class="dropdown-header">
                <span>Seleccionar rutas</span>
                <button class="close-menu-btn" @click="closeSelectMenu" aria-label="Cerrar menú">
                  <span class="material-icons">close</span>
                </button>
              </div>
              <div class="dropdown-item" @click="selectAllRoutes('all')" @keydown.enter="selectAllRoutes('all')"
                tabindex="0" role="button">
                <span class="material-icons">select_all</span>
                <span>Todas las rutas</span>
                <span class="item-badge">{{ totalRoutes }}</span>
              </div>
              <div class="dropdown-item" @click="selectAllRoutes('ENTRADA')" @keydown.enter="selectAllRoutes('ENTRADA')"
                tabindex="0" role="button">
                <span class="material-icons" style="color: #0d6efd;">login</span>
                <span>Todas las ENTRADAS</span>
                <span class="item-badge">{{ countRoutesByDirection('ENTRADA') }}</span>
              </div>
              <div class="dropdown-item" @click="selectAllRoutes('SALIDA')" @keydown.enter="selectAllRoutes('SALIDA')"
                tabindex="0" role="button">
                <span class="material-icons" style="color: #fd7e14;">logout</span>
                <span>Todas las SALIDAS</span>
                <span class="item-badge">{{ countRoutesByDirection('SALIDA') }}</span>
              </div>
              <div v-if="allRoutesSelected" class="dropdown-divider"></div>
              <div v-if="allRoutesSelected" class="dropdown-item danger" @click="deselectAllRoutes"
                @keydown.enter="deselectAllRoutes" tabindex="0" role="button">
                <span class="material-icons">remove_done</span>
                <span>Deseleccionar todas</span>
              </div>
            </div>
          </transition>
        </div>

        <button @click="toggleCollapse" class="action-btn" aria-label="Colapsar panel">
          <span class="material-icons">{{ isCollapsed ? 'chevron_left' : 'chevron_right' }}</span>
        </button>
      </div>
    </div>

    <div class="drag-handle" id="dragHandle"></div>

    <!-- Indicador de pintado en progreso -->
    <transition name="slide-down">
      <div v-if="isPainting" class="painting-progress">
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: paintingProgress + '%' }"></div>
        </div>
        <span class="progress-text">Pintando rutas: {{ routesPainted }}/{{ totalRoutesToPaint }}</span>
        <button @click="stopRoutePainting" class="stop-painting-btn" title="Detener (Doble clic)">
          <span class="material-icons">close</span>
        </button>
      </div>
    </transition>

    <!-- Buscador -->
    <transition name="slide-down">
      <div v-if="showSearch" class="search-section">
        <div class="search-input-container">
          <span class="material-icons search-icon">search</span>
          <input id="route-search-input" type="text" v-model="searchQuery" @input="performSearch"
            placeholder="...Buscar por ruta ..." class="search-input" ref="searchInput"
            aria-label="Buscar rutas" />
          <button v-if="searchQuery" @click="clearSearch" class="clear-search-btn" type="button"
            aria-label="Limpiar búsqueda">
            <span class="material-icons">close</span>
          </button>
        </div>

        <!-- Resultados de búsqueda -->
        <div v-if="searchResults.length > 0" class="search-results">
          <div v-for="result in searchResults" :key="result.clave_ruta" class="search-result-item"
            @click="selectRoute(result)" @keydown.enter="selectRoute(result)"
            @keydown.space.prevent="selectRoute(result)" role="button" tabindex="0"
            :aria-label="`Seleccionar ruta ${result.nombre_ruta}`">
            <span class="material-icons result-icon" :style="{ color: getRouteColor(result.clave_ruta) }">
              directions_bus
            </span>
            <div class="result-info">
              <span class="result-name">{{ result.nombre_ruta }}</span>
              <span class="result-detail">
                <span class="detail-item">
                  <span class="detail-label">Dirección:</span>
                  {{ result.direccion_ruta || 'No especificada' }}
                </span>
                <span class="detail-item">
                  <span class="detail-label">Inicio de ruta:</span>
                  {{ result.hora_inicio_ruta ? formatTime(result.hora_inicio_ruta) : 'No especificado' }}
                </span>
              </span>
            </div>
            <span class="result-badge">{{ result.paradas?.length || 0 }} paradas</span>
          </div>
        </div>
        <div v-else-if="searchQuery && !searching" class="no-results">
          No se encontraron rutas
        </div>
      </div>
    </transition>

    <!-- Contenido del árbol -->
    <div v-if="!isCollapsed" class="navigator-content">
      <!-- Filtros rápidos -->
      <div class="quick-filters">
        <button class="filter-chip" :class="{ 'active': activeFilter === 'all' }" @click="setFilter('all')"
          type="button">
          Todas
        </button>
        <button class="filter-chip" :class="{ 'active': activeFilter === 'ENTRADA' }" @click="setFilter('ENTRADA')"
          type="button">
          <span class="material-icons">login</span>
          Entradas
        </button>
        <button class="filter-chip" :class="{ 'active': activeFilter === 'SALIDA' }" @click="setFilter('SALIDA')"
          type="button">
          <span class="material-icons">logout</span>
          Salidas
        </button>
      </div>

      <!-- Árbol de rutas -->
      <div class="tree-container">
        <div v-for="group in filteredTreeData" :key="group.key" class="tree-group">
          <!-- Grupo principal (Rutas) -->
          <div class="group-header" @click="toggleGroup(group.key)" @keydown.enter="toggleGroup(group.key)"
            @keydown.space.prevent="toggleGroup(group.key)" role="button" tabindex="0"
            :aria-label="`${expandedGroups.includes(group.key) ? 'Colapsar' : 'Expandir'} ${group.key}`">
            <span class="material-icons group-icon">
              {{ expandedGroups.includes(group.key) ? 'expand_more' : 'chevron_right' }}
            </span>
            <span class="group-name">{{ group.key }}</span>
            <span class="group-count">{{ getGroupCount(group) }}</span>
          </div>

          <transition name="expand">
            <div v-if="expandedGroups.includes(group.key)" class="group-children">
              <!-- Subgrupos (Entradas / Salidas) -->
              <div v-for="subGroup in group.children" :key="subGroup.key" class="subgroup">
                <div class="subgroup-header" @click="toggleSubGroup(subGroup.key)"
                  @keydown.enter="toggleSubGroup(subGroup.key)" @keydown.space.prevent="toggleSubGroup(subGroup.key)"
                  role="button" tabindex="0"
                  :aria-label="`${expandedSubGroups.includes(subGroup.key) ? 'Colapsar' : 'Expandir'} ${subGroup.key}`">
                  <span class="material-icons subgroup-icon">
                    {{ expandedSubGroups.includes(subGroup.key) ? 'expand_more' : 'chevron_right' }}
                  </span>
                  <span class="subgroup-name">{{ subGroup.key }}</span>
                  <span class="subgroup-count">{{ subGroup.children?.length || 0 }}</span>
                </div>

                <transition name="expand">
                  <div v-if="expandedSubGroups.includes(subGroup.key)" class="subgroup-children">
                    <!-- Turnos -->
                    <div v-for="shiftGroup in subGroup.children" :key="shiftGroup.key" class="shift-group">
                      <div class="shift-header" @click="toggleShift(shiftGroup.key)"
                        @keydown.enter="toggleShift(shiftGroup.key)"
                        @keydown.space.prevent="toggleShift(shiftGroup.key)" role="button" tabindex="0"
                        :aria-label="`${expandedShifts.includes(shiftGroup.key) ? 'Colapsar' : 'Expandir'} ${shiftGroup.key}`">
                        <span class="material-icons shift-icon">
                          {{ expandedShifts.includes(shiftGroup.key) ? 'expand_more' : 'chevron_right' }}
                        </span>
                        <span class="shift-name">{{ shiftGroup.key }}</span>
                        <span class="shift-count">{{ shiftGroup.children?.length || 0 }}</span>
                      </div>

                      <transition name="expand">
                        <div v-if="expandedShifts.includes(shiftGroup.key)" class="shift-children">
                          <!-- Rutas -->
                          <div v-for="route in shiftGroup.children" :key="route.key" class="route-item"
                            :class="{ 'selected': props.selectedRoutes.includes(route.key) }"
                            @click="selectRouteFromTree(route)" @keydown.enter="selectRouteFromTree(route)"
                            @keydown.space.prevent="selectRouteFromTree(route)" role="button" tabindex="0"
                            :aria-label="`${route.name || route.key} - ${props.selectedRoutes.includes(route.key) ? 'seleccionada' : 'no seleccionada'}`">
                            <span class="route-color-dot" :style="{ backgroundColor: getRouteColor(route.key) }"></span>
                            <span class="route-name">{{ route.name || route.key }}</span>
                          </div>
                        </div>
                      </transition>
                    </div>
                  </div>
                </transition>
              </div>
            </div>
          </transition>
        </div>
      </div>

      <!-- Estadísticas -->
      <div class="stats-footer">
        <div class="stat-item">
          <span class="stat-value">{{ totalRoutes }}</span>
          <span class="stat-label">Rutas</span>
        </div>
        <div class="stat-item">
          <span class="stat-value">{{ props.selectedRoutes.length }}</span>
          <span class="stat-label">Seleccionadas</span>
        </div>
        <div class="stat-item">
          <span class="stat-value">{{ totalStops }}</span>
          <span class="stat-label">Paradas</span>
        </div>
      </div>
    </div>

    <!-- Versión colapsada -->
    <div v-else class="collapsed-content">
      <div class="collapsed-item" @click="toggleCollapse" @keydown.enter="toggleCollapse"
        @keydown.space.prevent="toggleCollapse" role="button" tabindex="0" title="Expandir panel"
        aria-label="Expandir panel">
        <span class="material-icons">menu</span>
      </div>
      <div class="collapsed-item" @click="toggleSearch" @keydown.enter="toggleSearch"
        @keydown.space.prevent="toggleSearch" :class="{ 'active': showSearch }" role="button" tabindex="0"
        title="Buscar" aria-label="Buscar">
        <span class="material-icons">search</span>
      </div>
      <div class="collapsed-stats">
        <span class="material-icons">route</span>
        <span class="collapsed-count">{{ props.selectedRoutes.length }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref, computed, watch, nextTick, onUnmounted,
} from 'vue';
import useSlideablePanel from '../composables/useSlideablePanel';

const props = defineProps({
  treeData: {
    type: Array,
    default: () => [],
  },
  selectedRoutes: {
    type: Array,
    default: () => [],
  },
  routeColors: {
    type: Object,
    default: () => ({}),
  },
  allRoutesFromApi: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['route-selected', 'route-deselected', 'stop-painting']);

// Estado
const isCollapsed = ref(false);
const showSearch = ref(false);
const searchQuery = ref('');
const searching = ref(false);
const searchResults = ref([]);
const expandedGroups = ref(['Rutas: Alpezzi Robles']);
const expandedSubGroups = ref(['Entradas', 'Salidas']);
const expandedShifts = ref([]);
const activeFilter = ref('all');
const searchInput = ref(null);

// Estado para el pintado de rutas
const isPainting = ref(false);
const paintingProgress = ref(0);
const routesPainted = ref(0);
const totalRoutesToPaint = ref(0);
const paintingTimeouts = ref([]);
const paintingInterval = ref(null);

// Toast notifications
const showToast = ref(false);
const toastMessage = ref('');
const toastType = ref('info');
const toastIcon = ref('ℹ');
let toastTimeout = null;

const selectMenuOpen = ref(false);
const routeNavigatorRef = ref(null);

useSlideablePanel(routeNavigatorRef);

const showToastMessage = (message, type = 'info', duration = 3000) => {
  if (toastTimeout) {
    clearTimeout(toastTimeout);
  }

  // Configurar icono según tipo
  const icons = {
    success: '✓',
    error: '✗',
    warning: '⚠',
    info: 'ℹ',
    paint: '🎨',
    stop: '⏹️',
  };

  toastMessage.value = message;
  toastType.value = type;
  toastIcon.value = icons[type] || 'ℹ';
  showToast.value = true;

  toastTimeout = setTimeout(() => {
    showToast.value = false;
    toastTimeout = null;
  }, duration);
};

const clearAllPaintingTimeouts = () => {
  paintingTimeouts.value.forEach((timeout) => clearTimeout(timeout));
  paintingTimeouts.value = [];
  if (paintingInterval.value) {
    clearInterval(paintingInterval.value);
    paintingInterval.value = null;
  }
};

const stopRoutePainting = () => {
  if (!isPainting.value) return;
  clearAllPaintingTimeouts();
  isPainting.value = false;

  showToastMessage(` Proceso detenido - ${routesPainted.value} de ${totalRoutesToPaint.value} rutas pintadas`, 'stop', 4000);

  emit('stop-painting');

  console.log(' Proceso de pintado detenido por el usuario');
};

const filteredTreeData = computed(() => {
  if (activeFilter.value === 'all') return props.treeData;

  return props.treeData.map((group) => ({
    ...group,
    children: group.children.map((subGroup) => ({
      ...subGroup,
      children: subGroup.children.map((shiftGroup) => ({
        ...shiftGroup,
        children: shiftGroup.children.filter((route) => {
          const routeData = props.allRoutesFromApi.find((r) => r.clave_ruta === route.key);
          return routeData?.direccion_ruta === activeFilter.value;
        }),
      })).filter((shiftGroup) => shiftGroup.children.length > 0),
    })).filter((subGroup) => subGroup.children.length > 0),
  })).filter((group) => group.children.length > 0);
});

const totalRoutes = computed(() => props.allRoutesFromApi.length);

const totalStops = computed(() => (
  props.allRoutesFromApi.reduce((acc, route) => acc + (route.paradas?.length || 0), 0)
));

const allRoutesSelected = computed(() => props.selectedRoutes.length === totalRoutes.value && totalRoutes.value > 0);

// METODOS
const formatTime = (time) => {
  if (!time) return '';
  return time.substring(0, 5);
};

const getRouteColor = (routeId) => (
  props.routeColors[routeId] || '#999'
);

const getGroupCount = (group) => {
  let count = 0;
  group.children?.forEach((subGroup) => {
    subGroup.children?.forEach((shiftGroup) => {
      count += shiftGroup.children?.length || 0;
    });
  });
  return count;
};

const toggleGroup = (groupKey) => {
  if (expandedGroups.value.includes(groupKey)) {
    expandedGroups.value = expandedGroups.value.filter((g) => g !== groupKey);
  } else {
    expandedGroups.value.push(groupKey);
  }
};

const toggleSubGroup = (subGroupKey) => {
  if (expandedSubGroups.value.includes(subGroupKey)) {
    expandedSubGroups.value = expandedSubGroups.value.filter((s) => s !== subGroupKey);
  } else {
    expandedSubGroups.value.push(subGroupKey);
  }
};

const toggleShift = (shiftKey) => {
  if (expandedShifts.value.includes(shiftKey)) {
    expandedShifts.value = expandedShifts.value.filter((s) => s !== shiftKey);
  } else {
    expandedShifts.value.push(shiftKey);
  }
};

const toggleCollapse = () => {
  if (window.innerWidth <= 480) {
    return;
  }
  isCollapsed.value = !isCollapsed.value;
  if (!isCollapsed.value && showSearch.value) {
    showSearch.value = false;
  }
};

const toggleSearch = async () => {
  showSearch.value = !showSearch.value;
  if (showSearch.value) {
    await nextTick();
    searchInput.value?.focus();
  } else {
    searchQuery.value = '';
    searchResults.value = [];
  }
};

const clearSearch = () => {
  searchQuery.value = '';
  searchResults.value = [];
  searchInput.value?.focus();
};

const performSearch = () => {
  searching.value = true;

  if (!searchQuery.value.trim()) {
    searchResults.value = [];
    searching.value = false;
    return;
  }

  const query = searchQuery.value.toLowerCase().trim();
  searchResults.value = props.allRoutesFromApi.filter(
    (route) => route.nombre_ruta?.toLowerCase().includes(query)
      || route.clave_ruta?.toLowerCase().includes(query),
  );

  searching.value = false;
};

const emitSelectedRoute = (route) => {
  console.log('Emitiendo ruta seleccionada:', route.nombre_ruta, 'con código:', route.clave_ruta);

  if (props.selectedRoutes.includes(route.clave_ruta)) {
    emit('route-deselected', route.clave_ruta);
  } else {
    emit('route-selected', route.clave_ruta);
  }
};

// ===== FUNCIONES PARA EL MENÚ DESPLEGABLE =====
const toggleSelectMenu = () => {
  if (isPainting.value) {
    showToastMessage('Ya hay un proceso de pintado en curso', 'warning', 2000);
    return;
  }
  selectMenuOpen.value = !selectMenuOpen.value;
};

const closeSelectMenu = () => {
  selectMenuOpen.value = false;
};

const countRoutesByDirection = (direction) => props.allRoutesFromApi.filter((route) => route.direccion_ruta === direction).length;

const deselectAllRoutes = () => {
  const rutasSeleccionadas = [...props.selectedRoutes];
  const cantidad = rutasSeleccionadas.length;

  if (cantidad === 0) {
    showToastMessage('No hay rutas seleccionadas', 'info', 2000);
    return;
  }

  showToastMessage(`Deseleccionando ${cantidad} ${cantidad === 1 ? 'ruta' : 'rutas'}...`, 'info', 2000);

  rutasSeleccionadas.forEach((routeId) => {
    emit('route-deselected', routeId);
  });

  setTimeout(() => {
    showToastMessage(`${cantidad} ${cantidad === 1 ? 'ruta deseleccionada' : 'rutas deseleccionadas'}`, 'success', 2000);
  }, 500);

  selectMenuOpen.value = false;
};

// ===== FUNCIÓN SELECT ALL MODIFICADA PARA ACEPTAR =====
const selectAllRoutes = (type = 'all') => {
  console.log('Seleccionando rutas tipo:', type);

  if (isPainting.value) {
    showToastMessage('Ya hay un proceso de pintado en curso. Use doble clic para detener', 'warning', 2000);
    return;
  }

  selectMenuOpen.value = false;

  // Determinar qué rutas seleccionar según el tipo
  let rutasParaSeleccionar;

  if (type === 'all') {
    rutasParaSeleccionar = props.allRoutesFromApi.filter(
      (route) => !props.selectedRoutes.includes(route.clave_ruta),
    );
  } else {
    rutasParaSeleccionar = props.allRoutesFromApi.filter(
      (route) => route.direccion_ruta === type && !props.selectedRoutes.includes(route.clave_ruta),
    );
  }

  const cantidad = rutasParaSeleccionar.length;

  if (cantidad === 0) {
    showToastMessage(`No hay rutas de ${type === 'all' ? 'este tipo' : type} para seleccionar`, 'info', 2000);
    return;
  }

  clearAllPaintingTimeouts();
  isPainting.value = true;
  routesPainted.value = 0;
  totalRoutesToPaint.value = cantidad;
  paintingProgress.value = 0;

  const tiempoInicio = Date.now();

  showToastMessage(`Seleccionando ${cantidad} rutas de ${type === 'all' ? 'todas' : type}...`, 'paint', 3000);

  paintingInterval.value = setInterval(() => {
    if (isPainting.value) {
      const tiempoTranscurrido = (Date.now() - tiempoInicio) / 1000;
      const progresoEsperado = (tiempoTranscurrido / 142) * 100;
      const progresoReal = (routesPainted.value / totalRoutesToPaint.value) * 100;
      paintingProgress.value = Math.min(100, Math.max(progresoReal, progresoEsperado));
    }
  }, 100);

  rutasParaSeleccionar.forEach((route, index) => {
    const tiempoPorRuta = 142000 / cantidad;
    const delay = index * tiempoPorRuta;

    const timeout = setTimeout(() => {
      if (!isPainting.value) {
        console.log('Proceso detenido');
        return;
      }

      console.log('Seleccionando:', route.clave_ruta);
      emit('route-selected', route.clave_ruta);
      routesPainted.value += 1;

      const progreso = (routesPainted.value / totalRoutesToPaint.value) * 100;
      if (progreso === 25 || progreso === 50 || progreso === 75) {
        showToastMessage(`Progreso: ${routesPainted.value}/${totalRoutesToPaint.value} rutas (${Math.round(progreso)}%)`, 'info', 2500);
      }
    }, delay);

    paintingTimeouts.value.push(timeout);
  });

  const tiempoTotalReal = 142000;

  const finalTimeout = setTimeout(() => {
    if (isPainting.value) {
      isPainting.value = false;
      clearInterval(paintingInterval.value);
      paintingInterval.value = null;

      if (routesPainted.value === cantidad) {
        showToastMessage(`✅ ${cantidad} rutas de ${type === 'all' ? 'todas' : type} cargadas correctamente`, 'success', 4000);
      } else {
        showToastMessage(`⚠️ Proceso completado: ${routesPainted.value}/${cantidad} rutas`, 'warning', 4000);
      }
    }
  }, tiempoTotalReal + 1000);

  paintingTimeouts.value.push(finalTimeout);
};

const selectRouteFromTree = (treeRoute) => {
  // Buscar la ruta por clave_ruta usando el key
  const fullRoute = props.allRoutesFromApi.find((r) => r.clave_ruta === treeRoute?.key);

  if (!fullRoute) {
    // Intentar buscar por nombre
    const routeByName = props.allRoutesFromApi.find((r) => r.nombre_ruta === treeRoute?.name);
    console.log(' [RouteNavigator] Búsqueda por nombre:', routeByName ? 'ENCONTRADA' : 'NO ENCONTRADA');

    if (routeByName) {
      console.log(' [RouteNavigator] Ruta encontrada por nombre:', routeByName.nombre_ruta, routeByName.clave_ruta);

      if (!props.selectedRoutes.includes(routeByName.clave_ruta)) {
        showToastMessage(`Seleccionando ruta: ${routeByName.nombre_ruta}`, 'info', 1500);
      }

      emitSelectedRoute(routeByName);
    } else {
      console.warn(' [RouteNavigator] No se encontró la ruta');
      console.log(' [RouteNavigator] Rutas disponibles:', props.allRoutesFromApi.map((r) => ({
        clave: r.clave_ruta,
        nombre: r.nombre_ruta,
      })));
    }
    return;
  }

  emitSelectedRoute(fullRoute);
};

const selectRoute = (route) => {
  emitSelectedRoute(route);

  showSearch.value = false;
  searchQuery.value = '';
  searchResults.value = [];
};

const setFilter = (filter) => {
  activeFilter.value = filter;
};

// WATCHERS
watch(activeFilter, () => {
  if (activeFilter.value !== 'all') {
    expandedGroups.value = ['Rutas: Alpezzi Robles'];
    expandedSubGroups.value = ['Entradas', 'Salidas'];
    expandedShifts.value = []; //
  }
});

watch(() => props.selectedRoutes, () => {
  if (allRoutesSelected.value && isPainting.value) {
    clearAllPaintingTimeouts();
    isPainting.value = false;
    paintingProgress.value = 100;
  }
});

onUnmounted(() => {
  clearAllPaintingTimeouts();
  if (toastTimeout) {
    clearTimeout(toastTimeout);
  }
});

</script>

<style scoped>
@import url('https://fonts.googleapis.com/icon?family=Material+Icons');
@import '@/styles/components/route-navigator.css';

</style>
